/**
 * NeoCorePlazoFijoDetallePage — vista completa de un Plazo Fijo.
 *
 * Hilo del flujo:
 *   1. Operador crea PF (pendiente_de_caja, saldo 0).
 *   2. Operador confirma ingreso → activa el PF (saldo sube a capital).
 *   3. PF activo genera intereses según calendario.
 *   4. Al vencer, renueva o liquida.
 *
 * Esta página es donde el operador convive con el PF a lo largo de su vida:
 *   - Pendiente: ve qué falta (registrar ingreso) y puede cancelar/eliminar.
 *   - Activo: ve calendario de pagos, beneficiarios, movimientos.
 *   - Vencido/cancelado: ve histórico cerrado.
 *
 * Asociado siempre visible arriba con link a su fiche.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowsClockwise,
  CaretDown,
  CaretRight,
  CheckCircle,
  ClockCounterClockwise,
  Hourglass,
  IdentificationCard,
  ListChecks,
  Medal,
  Printer,
  Receipt,
  Users,
  Warning,
  XCircle,
  Coins,
} from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { ConfirmarIngresoPfModal } from '@/components/ConfirmarIngresoPfModal'
import { ReinvertirPfModal } from '@/components/ReinvertirPfModal'
import { reimprimirComprobantePorMovimientoId } from '@/lib/reimprimirComprobante'
import { imprimirCertificadoPorPlazoFijoId } from '@/lib/imprimirCdp'

type DetallePF = {
  id: string
  cuenta_id: string
  asociado_id: string
  capital_inicial: number
  tasa_anual: number
  plazo_dias: number
  manejo_capitalizacion: 'MENSUAL' | 'AL_VENCIMIENTO'
  modalidad_renovacion: string
  cuenta_acreditacion_id: string | null
  origen_fondos: string | null
  fecha_apertura: string
  fecha_inicio: string
  fecha_vencimiento: string
  fecha_proximo_pago: string | null
  estado_pf: string
  certificado_no: string | null
  tasa_override: boolean
  tasa_override_motivo: string | null
}

type Asociado = {
  id: string
  no_asociado: string
  nombre_completo: string
  dpi: string
}

type Beneficiario = {
  id: string
  nombre_completo: string
  identificacion: string
  parentesco: string
  porcentaje: number
  orden: number
}

type Pago = {
  id: string
  numero_pago: number
  fecha_pago: string
  dias_periodo: number
  interes_generado: number
  isr_descuento: number
  total_neto: number
  estado: 'pendiente' | 'acreditado' | 'cancelado'
  acreditado_at: string | null
}

// Movimientos del PF leídos del modelo legacy: movimiento_cuenta enlaza con
// movimiento_caja (cabecera con tipo + correlativo + serie RIG/REG). El frontend
// se queda con una vista simplificada para el feed.
type Movimiento = {
  id: string
  monto: number
  saldo_anterior: number | null
  saldo_nuevo: number | null
  descripcion: string | null
  fecha: string
  rig_referencia: string | null
  tipo_movimiento_codigo: string | null
  movimiento_caja_id: string | null
}

// Reporte de pago hecho por el asociado desde la app (migración 0052)
type ReportePagoLite = {
  id: string
  forma_pago: string
  monto: number
  numero_referencia: string | null
  fecha_movimiento: string
  estado: 'pendiente' | 'en_revision' | 'conciliado' | 'rechazado'
  comprobante_storage_path: string | null
  notas_asociado: string | null
  created_at: string
}

const ESTADO_META: Record<string, { label: string; color: string; tone: string }> = {
  solicitado: { label: 'Solicitado', color: '#6B6B6B', tone: 'neutral' },
  pendiente_de_caja: { label: 'Pendiente de ingreso', color: '#B8860B', tone: 'warn' },
  activo: { label: 'Activo', color: '#0F8A57', tone: 'positive' },
  vencido: { label: 'Vencido', color: '#D4A017', tone: 'warn' },
  renovado: { label: 'Renovado', color: '#0F8A57', tone: 'positive' },
  liquidado: { label: 'Liquidado', color: '#888780', tone: 'neutral' },
  cancelado_anticipado: { label: 'Cancelado', color: '#A32D2D', tone: 'danger' },
  rechazado: { label: 'Rechazado', color: '#A32D2D', tone: 'danger' },
}

const MANEJO_LABEL: Record<string, string> = {
  MENSUAL: 'Mensual a cuenta de acreditación',
  AL_VENCIMIENTO: 'Al vencimiento',
}

const ORIGEN_LABEL: Record<string, string> = {
  CAJA: 'Depósito en caja',
  DEBITO_AHORRO_CORRIENTE: 'Débito de Ahorro corriente',
}

// Map tipo_movimiento_caja → label. PF usa E21 para apertura; otros van a venir
// cuando Fase 2 enchufe acreditación de intereses (E?? a definir) y cancelación
// (REG via S??).
const TIPO_MOV_LABEL: Record<string, string> = {
  E21: 'Ingreso de apertura',
  E13: 'Depósito',
  E28: 'Aportación',
  S4: 'Egreso',
}

function formatQ(n: number | string): string {
  const num = typeof n === 'string' ? parseFloat(n) : n
  return `Q ${num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

// Columnas DATE ('YYYY-MM-DD'): anclar mediodía para no correrse un día en GT (UTC-6)
function anclarFecha(iso: string): Date {
  return new Date(iso.length === 10 ? iso + 'T12:00:00' : iso)
}

function formatFecha(iso: string): string {
  return anclarFecha(iso).toLocaleDateString('es-GT', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })
}

function formatFechaCorta(iso: string): string {
  return anclarFecha(iso).toLocaleDateString('es-GT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

export function NeoCorePlazoFijoDetallePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [pf, setPf] = useState<DetallePF | null>(null)
  const [asociado, setAsociado] = useState<Asociado | null>(null)
  const [beneficiarios, setBeneficiarios] = useState<Beneficiario[]>([])
  const [pagos, setPagos] = useState<Pago[]>([])
  const [movimientos, setMovimientos] = useState<Movimiento[]>([])
  // Reportes de pago que el asociado mandó desde la app — cola informativa
  // del operador. (migración 0052)
  const [reportes, setReportes] = useState<ReportePagoLite[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [confirmarOpen, setConfirmarOpen] = useState(false)
  const [reinvertirOpen, setReinvertirOpen] = useState(false)
  const [cancelando, setCancelando] = useState(false)
  const [imprimiendoApertura, setImprimiendoApertura] = useState(false)
  const [imprimiendoCert, setImprimiendoCert] = useState(false)
  // Hugo: "es más importante y más luxury cambiar de vista [del calendario] y
  // si quieren ver la tabla que la puedan extender". Por defecto, vista resumen.
  const [calendarioExpandido, setCalendarioExpandido] = useState(false)

  const cargar = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError(null)
    try {
      const [pfRes, benefRes, pagosRes, movsRes, reportesRes] = await Promise.all([
        supabase.from('plazo_fijo_detalle').select('*').eq('id', id).maybeSingle(),
        supabase.from('plazo_fijo_beneficiario').select('*').eq('plazo_fijo_id', id).order('orden'),
        supabase.from('plazo_fijo_calendario_pago').select('*').eq('plazo_fijo_id', id).order('numero_pago'),
        // Modelo legacy: feed leído de movimiento_cuenta + join al movimiento_caja
        // (cabecera) y a serie_documento (alias 'RIG'/'REG' para construir el folio).
        supabase
          .from('movimiento_cuenta')
          .select(
            'id, monto, saldo_anterior, saldo_nuevo, descripcion, fecha, movimiento_caja_id, movimiento_caja:movimiento_caja_id(correlativo, tipo_movimiento_codigo, serie:serie_documento_id(alias))',
          )
          .eq('plazo_fijo_id', id)
          .order('fecha', { ascending: false }),
        supabase
          .from('reporte_pago_asociado')
          .select(
            'id, forma_pago, monto, numero_referencia, fecha_movimiento, estado, comprobante_storage_path, notas_asociado, created_at',
          )
          .eq('plazo_fijo_id', id)
          .order('created_at', { ascending: false }),
      ])
      if (pfRes.error) throw pfRes.error
      if (!pfRes.data) {
        setError('Plazo fijo no encontrado.')
        return
      }
      setPf(pfRes.data as DetallePF)
      setBeneficiarios((benefRes.data ?? []) as Beneficiario[])
      setPagos((pagosRes.data ?? []) as Pago[])
      // Mapear el shape de PostgREST → Movimiento simple
      const movs: Movimiento[] = (movsRes.data ?? []).map((row: any) => {
        const mc = row.movimiento_caja
        const alias = mc?.serie?.alias ?? null
        const corr = mc?.correlativo ?? null
        return {
          id: row.id,
          monto: Number(row.monto),
          saldo_anterior: row.saldo_anterior !== null ? Number(row.saldo_anterior) : null,
          saldo_nuevo: row.saldo_nuevo !== null ? Number(row.saldo_nuevo) : null,
          descripcion: row.descripcion,
          fecha: row.fecha,
          rig_referencia:
            alias && corr !== null ? `${alias}-${String(corr).padStart(6, '0')}` : null,
          tipo_movimiento_codigo: mc?.tipo_movimiento_codigo ?? null,
          movimiento_caja_id: row.movimiento_caja_id ?? null,
        }
      })
      setMovimientos(movs)
      setReportes(((reportesRes?.data ?? []) as any[]).map((r) => ({
        id: r.id,
        forma_pago: r.forma_pago,
        monto: Number(r.monto),
        numero_referencia: r.numero_referencia,
        fecha_movimiento: r.fecha_movimiento,
        estado: r.estado,
        comprobante_storage_path: r.comprobante_storage_path,
        notas_asociado: r.notas_asociado,
        created_at: r.created_at,
      })))

      const { data: asoc } = await supabase
        .from('asociado')
        .select('id, no_asociado, nombre_completo, dpi')
        .eq('id', (pfRes.data as DetallePF).asociado_id)
        .maybeSingle()
      setAsociado((asoc ?? null) as Asociado | null)
    } catch (e: any) {
      setError(e?.message ?? 'No pudimos cargar el plazo fijo.')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    cargar()
  }, [cargar])

  const onCancelar = async () => {
    if (!pf) return
    const sure = window.confirm(
      pf.estado_pf === 'pendiente_de_caja'
        ? '¿Eliminar este plazo fijo?\n\nNo se ha registrado ingreso, así que se borra completamente. Esta acción no se puede deshacer.'
        : '¿Cancelar este plazo fijo?\n\nLa cuenta se cierra y el saldo se devuelve. La penalización por retiro anticipado (recálculo a 4%) se aplicará en una iteración posterior cuando el motor contable esté listo.\n\n¿Continuar?',
    )
    if (!sure) return
    setCancelando(true)
    try {
      const motivo = window.prompt('Motivo de la cancelación (opcional):') ?? null
      const { error: rpcErr } = await supabase.rpc('cancelar_pf_admin', {
        p_plazo_fijo_id: pf.id,
        p_motivo: motivo,
      })
      if (rpcErr) {
        alert(`Error: ${rpcErr.message}`)
        return
      }
      // Si era pendiente_de_caja, ya no existe → volver al listado
      if (pf.estado_pf === 'pendiente_de_caja') {
        navigate('/neocore/plazos-fijos')
      } else {
        cargar()
      }
    } finally {
      setCancelando(false)
    }
  }

  if (loading) {
    return <div style={{ padding: 40, color: 'var(--n-500)' }}>Cargando…</div>
  }
  if (error || !pf) {
    return (
      <div style={{ padding: 40, color: '#A32D2D' }}>
        {error ?? 'No encontramos este plazo fijo.'}
      </div>
    )
  }

  const estadoMeta = ESTADO_META[pf.estado_pf] ?? { label: pf.estado_pf, color: '#6B6B6B', tone: 'neutral' }
  const isPendiente = pf.estado_pf === 'pendiente_de_caja'
  const isActivo = pf.estado_pf === 'activo'
  const puedeCancelar = isPendiente || isActivo
  const tasaPct = (Number(pf.tasa_anual) * 100).toFixed(2)

  // Días restantes (solo si activo)
  let diasRestantes = 0
  let pctTranscurrido = 0
  if (isActivo) {
    const hoy = new Date()
    const fin = anclarFecha(pf.fecha_vencimiento)
    diasRestantes = Math.max(0, Math.round((fin.getTime() - hoy.getTime()) / 86400000))
    const totalDias = pf.plazo_dias
    const transcurridos = totalDias - diasRestantes
    pctTranscurrido = Math.min(100, Math.max(0, (transcurridos / totalDias) * 100))
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      {/* Breadcrumb + acciones */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <Link
          to="/neocore/plazos-fijos"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            color: 'var(--n-500)',
            textDecoration: 'none',
          }}
        >
          <ArrowLeft size={12} />
          Plazos fijos
        </Link>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {isPendiente ? (
            <button
              className="btn btn-primary"
              onClick={() => setConfirmarOpen(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <CheckCircle size={14} weight="bold" />
              Confirmar ingreso
            </button>
          ) : null}
          {/* Botón "Imprimir comprobante" prominente cuando hay un ingreso ya
              registrado. Hugo: "no veo en ningún lado el recibo de ingresos
              durante el flujo de la apertura". */}
          {(() => {
            const apertura = movimientos.find(
              (m) => m.tipo_movimiento_codigo === 'E21' && m.movimiento_caja_id,
            )
            if (!apertura) return null
            return (
              <button
                className="btn btn-ghost"
                disabled={imprimiendoApertura}
                onClick={async () => {
                  if (!apertura.movimiento_caja_id) return
                  setImprimiendoApertura(true)
                  try {
                    await reimprimirComprobantePorMovimientoId(apertura.movimiento_caja_id)
                  } finally {
                    setImprimiendoApertura(false)
                  }
                }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <Printer size={14} weight="bold" />
                {imprimiendoApertura ? 'Abriendo…' : 'Imprimir comprobante'}
              </button>
            )
          })()}
          {/* Certificado de Inversión — disponible una vez confirmado el ingreso
              del PF (no se emite mientras está pendiente_de_caja). Abre una
              ventana lista para imprimir o guardar como PDF. */}
          {!isPendiente ? (
            <button
              className="btn btn-ghost"
              disabled={imprimiendoCert}
              onClick={async () => {
                if (!pf.id) return
                setImprimiendoCert(true)
                try {
                  const r = await imprimirCertificadoPorPlazoFijoId(pf.id)
                  if (!r.ok) {
                    // eslint-disable-next-line no-alert
                    alert(`No pudimos generar el certificado: ${r.error}`)
                  }
                } finally {
                  setImprimiendoCert(false)
                }
              }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Medal size={14} weight="bold" />
              {imprimiendoCert ? 'Generando…' : 'Generar certificado'}
            </button>
          ) : null}
          {isActivo ? (
            <button
              className="btn btn-ghost"
              onClick={() => setReinvertirOpen(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <ArrowsClockwise size={14} weight="bold" />
              Reinvertir
            </button>
          ) : null}
          {puedeCancelar ? (
            <button
              className="btn btn-ghost"
              onClick={onCancelar}
              disabled={cancelando}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                color: '#A32D2D',
              }}
            >
              <XCircle size={14} weight="bold" />
              {isPendiente ? 'Eliminar' : 'Cancelar plazo'}
            </button>
          ) : null}
        </div>
      </div>

      {/* Asociado al centro — siempre visible arriba */}
      {asociado ? (
        <Link
          to={`/neocore/asociados/${asociado.id}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: '14px 18px',
            background: 'var(--surface)',
            borderRadius: 12,
            border: '1px solid var(--border)',
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 22,
              background: 'rgba(240,78,35,0.10)',
              color: 'var(--brand-500)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IdentificationCard size={20} weight="duotone" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--n-900)' }}>
              {asociado.nombre_completo}
            </div>
            <div style={{ fontSize: 12, color: 'var(--n-500)', marginTop: 2 }}>
              No. asociado <strong>{asociado.no_asociado}</strong> · DPI {asociado.dpi}
            </div>
          </div>
          <CaretRight size={14} color="var(--n-400)" weight="bold" />
        </Link>
      ) : null}

      {/* Hero — capital + estado + certificado */}
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 16,
          border: '1px solid var(--border)',
          padding: '24px 26px',
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div
              style={{
                fontSize: 11,
                color: 'var(--n-500)',
                fontWeight: 600,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
              }}
            >
              Capital del plazo fijo
            </div>
            <div
              style={{
                fontSize: 40,
                fontWeight: 700,
                color: 'var(--n-900)',
                letterSpacing: -1.2,
                marginTop: 4,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatQ(pf.capital_inicial)}
            </div>
            <div style={{ display: 'flex', gap: 14, marginTop: 8, flexWrap: 'wrap' }}>
              <Pill label={`${tasaPct}% anual`} tone="brand" />
              <Pill label={`${pf.plazo_dias} días`} tone="neutral" />
              <Pill label={MANEJO_LABEL[pf.manejo_capitalizacion]} tone="neutral" />
              {pf.tasa_override ? (
                <Pill label="Tasa especial" tone="warn" />
              ) : null}
            </div>

            {/* Rendimiento proyectado — Hugo: "lo más importante que es cuánto va
                a generar aún no está". Lo subimos al hero, no escondido en el
                calendario. El monto es el neto post-ISR (ya descontado 10%). */}
            {pagos.length > 0 ? (
              <div
                style={{
                  marginTop: 16,
                  display: 'inline-flex',
                  alignItems: 'baseline',
                  gap: 10,
                  padding: '10px 14px',
                  borderRadius: 12,
                  background: 'rgba(15,138,87,0.08)',
                  border: '1px solid rgba(15,138,87,0.18)',
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: '#0F8A57',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  {pf.manejo_capitalizacion === 'AL_VENCIMIENTO'
                    ? 'Va a generar al vencimiento'
                    : 'Va a generar en total'}
                </span>
                <span
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: '#0F8A57',
                    letterSpacing: -0.5,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  + {formatQ(pagos.reduce((s, p) => s + Number(p.total_neto), 0))}
                </span>
                <span style={{ fontSize: 11, color: 'var(--n-500)' }}>
                  ya descontado el 10% ISR
                </span>
              </div>
            ) : null}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 14px',
                borderRadius: 999,
                background: `${estadoMeta.color}1A`,
                color: estadoMeta.color,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 3,
                  background: estadoMeta.color,
                  display: 'inline-block',
                }}
              />
              {estadoMeta.label}
            </div>
            {pf.certificado_no ? (
              <div
                style={{
                  fontFamily: 'ui-monospace, Menlo, monospace',
                  fontSize: 11,
                  color: 'var(--n-500)',
                  letterSpacing: 0.5,
                }}
              >
                {pf.certificado_no}
              </div>
            ) : null}
          </div>
        </div>

        {/* Banda de progreso si está activo */}
        {isActivo ? (
          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 6,
              }}
            >
              <span style={{ fontSize: 11, color: 'var(--n-500)' }}>
                {Math.round(pctTranscurrido)}% del plazo transcurrido
              </span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--n-900)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {diasRestantes} {diasRestantes === 1 ? 'día' : 'días'} restantes
              </span>
            </div>
            <div
              style={{
                height: 6,
                borderRadius: 3,
                background: 'rgba(240,78,35,0.12)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${pctTranscurrido}%`,
                  height: '100%',
                  background: 'var(--brand-500)',
                }}
              />
            </div>
          </div>
        ) : null}

        {/* Banner pendiente de caja */}
        {isPendiente ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              padding: 14,
              borderRadius: 12,
              background: 'rgba(184,134,11,0.08)',
              border: '1px solid rgba(184,134,11,0.22)',
            }}
          >
            <Warning size={20} weight="duotone" color="#B8860B" />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--n-900)' }}>
                Falta registrar el ingreso
              </div>
              <div style={{ fontSize: 12, color: 'var(--n-700)', marginTop: 2, lineHeight: 1.5 }}>
                Este plazo fijo está creado pero todavía no recibió el dinero. Cuando lo
                registres en caja (efectivo, depósito o transferencia), el plazo se activa
                y empieza a generar intereses.
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Términos y fechas */}
      <Section
        title="Términos del plazo"
        Icon={ListChecks}
      >
        <DataGrid>
          <DataItem label="Cuenta del PF" value={pf.cuenta_id} mono />
          <DataItem label="Origen de fondos" value={pf.origen_fondos ? ORIGEN_LABEL[pf.origen_fondos] ?? pf.origen_fondos : '—'} />
          <DataItem label="Fecha de apertura" value={formatFecha(pf.fecha_apertura)} />
          <DataItem label="Fecha de inicio" value={formatFecha(pf.fecha_inicio)} />
          <DataItem label="Vencimiento" value={formatFecha(pf.fecha_vencimiento)} />
          {pf.fecha_proximo_pago ? (
            <DataItem label="Próximo pago" value={formatFecha(pf.fecha_proximo_pago)} />
          ) : null}
          <DataItem
            label="Cuenta de acreditación"
            value={pf.cuenta_acreditacion_id ?? '—'}
            mono={!!pf.cuenta_acreditacion_id}
          />
          <DataItem
            label="Renovación al vencer"
            value={pf.modalidad_renovacion === 'AUTOMATICA' ? 'Automática (5 días hábiles)' : 'Manual'}
          />
        </DataGrid>
        {pf.tasa_override && pf.tasa_override_motivo ? (
          <div
            style={{
              marginTop: 14,
              padding: 12,
              borderRadius: 10,
              background: 'rgba(184,134,11,0.06)',
              border: '1px solid rgba(184,134,11,0.20)',
              fontSize: 12,
              color: 'var(--n-700)',
            }}
          >
            <strong>Tasa especial autorizada:</strong> {pf.tasa_override_motivo}
          </div>
        ) : null}
      </Section>

      {/* Beneficiarios */}
      <Section
        title="Beneficiarios"
        Icon={Users}
        hint={
          beneficiarios.length === 0
            ? 'Sin beneficiarios designados. En caso de fallecimiento del asociado, se aplica la regulación INGECOP.'
            : `${beneficiarios.length} ${beneficiarios.length === 1 ? 'beneficiario designado' : 'beneficiarios designados'}.`
        }
      >
        {beneficiarios.length === 0 ? (
          <Empty />
        ) : (
          <div className="card-table">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Identificación</th>
                  <th>Parentesco</th>
                  <th style={{ textAlign: 'right' }}>Porcentaje</th>
                </tr>
              </thead>
              <tbody>
                {beneficiarios.map((b) => (
                  <tr key={b.id}>
                    <td style={{ fontWeight: 500 }}>{b.nombre_completo}</td>
                    <td style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }}>
                      {b.identificacion}
                    </td>
                    <td>{b.parentesco}</td>
                    <td
                      style={{
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: 600,
                      }}
                    >
                      {Number(b.porcentaje).toFixed(0)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Calendario de pagos — vista resumen por defecto, tabla expandible */}
      <Section
        title="Calendario de pagos"
        Icon={Hourglass}
        hint={
          pf.manejo_capitalizacion === 'AL_VENCIMIENTO'
            ? 'Pago único al vencimiento.'
            : `${pagos.length} pagos mensuales proyectados.`
        }
      >
        {pagos.length === 0 ? (
          <Empty />
        ) : (
          <CalendarioResumen
            pagos={pagos}
            isAlVencimiento={pf.manejo_capitalizacion === 'AL_VENCIMIENTO'}
            expandido={calendarioExpandido}
            onToggle={() => setCalendarioExpandido((v) => !v)}
          />
        )}
        {pagos.length > 0 && calendarioExpandido && (
          <div className="card-table" style={{ marginTop: 12 }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Fecha</th>
                  <th style={{ textAlign: 'right' }}>Días</th>
                  <th style={{ textAlign: 'right' }}>Interés</th>
                  <th style={{ textAlign: 'right' }}>ISR</th>
                  <th style={{ textAlign: 'right' }}>Neto</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {pagos.map((p) => (
                  <tr key={p.id}>
                    <td style={{ color: 'var(--n-500)', fontVariantNumeric: 'tabular-nums' }}>
                      {String(p.numero_pago).padStart(2, '0')}
                    </td>
                    <td>{formatFechaCorta(p.fecha_pago)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {p.dias_periodo}
                    </td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {formatQ(p.interes_generado)}
                    </td>
                    <td
                      style={{
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                        color: 'var(--n-500)',
                      }}
                    >
                      − {formatQ(p.isr_descuento)}
                    </td>
                    <td
                      style={{
                        textAlign: 'right',
                        fontWeight: 600,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {formatQ(p.total_neto)}
                    </td>
                    <td>
                      <PagoEstado estado={p.estado} acreditadoAt={p.acreditado_at} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Reportes del asociado (depósitos / boletas que él subió desde la app) */}
      {reportes.length > 0 && (
        <Section
          title="Reportes del asociado"
          Icon={Receipt}
          hint={`${reportes.length} reporte${reportes.length === 1 ? '' : 's'} desde la app móvil. Verificá la boleta y el monto antes de confirmar el ingreso.`}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {reportes.map((r) => (
              <ReporteRow key={r.id} reporte={r} />
            ))}
          </div>
        </Section>
      )}

      {/* Movimientos relacionados */}
      <Section
        title="Movimientos relacionados"
        Icon={Receipt}
        hint={
          movimientos.length === 0
            ? 'Aún no hay movimientos registrados. El primero se crea cuando confirmes el ingreso de apertura.'
            : `${movimientos.length} ${movimientos.length === 1 ? 'movimiento' : 'movimientos'} en el historial.`
        }
      >
        {movimientos.length === 0 ? (
          <Empty />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {movimientos.map((m) => (
              <MovimientoRow key={m.id} mov={m} />
            ))}
          </div>
        )}
      </Section>

      {/* Modal confirmar ingreso */}
      <ConfirmarIngresoPfModal
        open={confirmarOpen}
        plazoFijo={
          pf
            ? {
                id: pf.id,
                asociado_id: pf.asociado_id,
                capital: Number(pf.capital_inicial),
                origen_fondos: pf.origen_fondos,
                cuenta_acreditacion_id: pf.cuenta_acreditacion_id,
                certificado_no: pf.certificado_no,
              }
            : null
        }
        onClose={() => setConfirmarOpen(false)}
        onConfirmed={() => {
          setConfirmarOpen(false)
          cargar()
        }}
      />

      {/* Modal reinvertir / renegociar (solo PF activo) */}
      <ReinvertirPfModal
        open={reinvertirOpen}
        plazoFijo={
          pf
            ? {
                id: pf.id,
                asociado_id: pf.asociado_id,
                capital: Number(pf.capital_inicial),
                tasa_anual: Number(pf.tasa_anual),
                certificado_no: pf.certificado_no,
                cuenta_acreditacion_id: pf.cuenta_acreditacion_id,
              }
            : null
        }
        onClose={() => setReinvertirOpen(false)}
        onReinvested={() => {
          setReinvertirOpen(false)
          navigate('/neocore/plazos-fijos')
        }}
      />
    </div>
  )
}

function Section({
  title,
  Icon,
  hint,
  children,
}: {
  title: string
  Icon: typeof ListChecks
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Icon size={16} weight="duotone" color="var(--brand-500)" />
        <h3
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: 'var(--n-900)',
            margin: 0,
            letterSpacing: 0.1,
          }}
        >
          {title}
        </h3>
        {hint ? (
          <span style={{ fontSize: 11, color: 'var(--n-500)', marginLeft: 'auto' }}>
            {hint}
          </span>
        ) : null}
      </div>
      {children}
    </div>
  )
}

function DataGrid({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 14,
        padding: 16,
        background: 'var(--surface)',
        borderRadius: 12,
        border: '1px solid var(--border)',
      }}
    >
      {children}
    </div>
  )
}

function DataItem({
  label,
  value,
  mono,
}: {
  label: string
  value: string
  mono?: boolean
}) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--n-500)', marginBottom: 3, fontWeight: 500 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 13,
          color: 'var(--n-900)',
          fontWeight: 500,
          fontFamily: mono ? 'ui-monospace, Menlo, monospace' : undefined,
          fontVariantNumeric: mono ? 'tabular-nums' : undefined,
        }}
      >
        {value}
      </div>
    </div>
  )
}

function Pill({
  label,
  tone,
}: {
  label: string
  tone: 'brand' | 'neutral' | 'warn'
}) {
  const styles: Record<string, { bg: string; fg: string }> = {
    brand: { bg: 'rgba(240,78,35,0.10)', fg: '#F04E23' },
    neutral: { bg: 'var(--n-100)', fg: 'var(--n-700)' },
    warn: { bg: 'rgba(184,134,11,0.10)', fg: '#B8860B' },
  }
  const s = styles[tone]
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 10px',
        borderRadius: 999,
        background: s.bg,
        color: s.fg,
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  )
}

function PagoEstado({ estado, acreditadoAt }: { estado: string; acreditadoAt: string | null }) {
  if (estado === 'acreditado') {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          fontSize: 11,
          color: '#0F8A57',
          fontWeight: 600,
        }}
      >
        <CheckCircle size={12} weight="fill" />
        Acreditado
        {acreditadoAt ? (
          <span style={{ color: 'var(--n-500)', fontWeight: 400, marginLeft: 4 }}>
            {formatFechaCorta(acreditadoAt)}
          </span>
        ) : null}
      </span>
    )
  }
  if (estado === 'cancelado') {
    return (
      <span style={{ fontSize: 11, color: 'var(--n-500)' }}>Cancelado</span>
    )
  }
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 11,
        color: 'var(--n-500)',
      }}
    >
      <ClockCounterClockwise size={12} />
      Pendiente
    </span>
  )
}

function MovimientoRow({ mov }: { mov: Movimiento }) {
  const [imprimiendo, setImprimiendo] = useState(false)
  const tipoLabel = mov.tipo_movimiento_codigo
    ? TIPO_MOV_LABEL[mov.tipo_movimiento_codigo] ?? mov.tipo_movimiento_codigo
    : 'Movimiento'
  const esIngreso = mov.monto >= 0

  const onImprimir = async () => {
    if (!mov.movimiento_caja_id || imprimiendo) return
    setImprimiendo(true)
    try {
      await reimprimirComprobantePorMovimientoId(mov.movimiento_caja_id)
    } finally {
      setImprimiendo(false)
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        background: 'var(--surface)',
        borderRadius: 10,
        border: '1px solid var(--border)',
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 16,
          background: esIngreso ? 'rgba(15,138,87,0.10)' : 'rgba(163,45,45,0.10)',
          color: esIngreso ? '#0F8A57' : '#A32D2D',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Coins size={14} weight="duotone" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--n-900)' }}>{tipoLabel}</div>
        <div style={{ fontSize: 11, color: 'var(--n-500)', marginTop: 2 }}>
          {formatFechaCorta(mov.fecha)}
          {mov.rig_referencia ? <span> · {mov.rig_referencia}</span> : null}
        </div>
        {mov.descripcion ? (
          <div style={{ fontSize: 11, color: 'var(--n-700)', marginTop: 4 }}>{mov.descripcion}</div>
        ) : null}
      </div>
      <div
        style={{
          textAlign: 'right',
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          color: esIngreso ? '#0F8A57' : '#A32D2D',
          fontSize: 14,
        }}
      >
        {esIngreso ? '+' : '−'} {formatQ(Math.abs(mov.monto))}
      </div>
      {mov.movimiento_caja_id ? (
        <button
          onClick={onImprimir}
          disabled={imprimiendo}
          className="btn btn-ghost"
          title="Reimprimir comprobante"
          style={{
            width: 32,
            height: 32,
            padding: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Printer size={14} weight="bold" />
        </button>
      ) : null}
    </div>
  )
}

function Empty() {
  return (
    <div
      style={{
        padding: 20,
        background: 'var(--surface)',
        borderRadius: 10,
        border: '1px dashed var(--border)',
        color: 'var(--n-500)',
        fontSize: 12,
        textAlign: 'center',
      }}
    >
      Sin información todavía.
    </div>
  )
}

/**
 * Vista resumen del calendario — Hugo: "es más importante y más luxury cambiar
 * de vista [del calendario] y si quieren ver la tabla que la puedan extender".
 * Muestra 4 KPIs y CTA para expandir la tabla completa.
 */
function CalendarioResumen({
  pagos,
  isAlVencimiento,
  expandido,
  onToggle,
}: {
  pagos: Pago[]
  isAlVencimiento: boolean
  expandido: boolean
  onToggle: () => void
}) {
  const totalInteresBruto = pagos.reduce((s, p) => s + Number(p.interes_generado), 0)
  const totalIsr = pagos.reduce((s, p) => s + Number(p.isr_descuento), 0)
  const totalNeto = pagos.reduce((s, p) => s + Number(p.total_neto), 0)
  const proximo = pagos.find((p) => p.estado === 'pendiente')

  const kpi: Array<{ label: string; value: string; color?: string }> = [
    {
      label: isAlVencimiento ? 'Pago al vencimiento' : 'Próximo pago',
      value: proximo ? formatFechaCorta(proximo.fecha_pago) : '—',
    },
    { label: 'Interés bruto', value: formatQ(totalInteresBruto) },
    { label: 'ISR', value: '− ' + formatQ(totalIsr), color: 'var(--n-500)' },
    { label: 'Total neto', value: formatQ(totalNeto), color: '#0F8A57' },
  ]

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 10,
        }}
      >
        {kpi.map((k) => (
          <div
            key={k.label}
            style={{
              padding: 14,
              borderRadius: 12,
              background: 'var(--surface)',
              border: '1px solid var(--border)',
            }}
          >
            <div
              style={{
                fontSize: 10,
                color: 'var(--n-500)',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontWeight: 700,
              }}
            >
              {k.label}
            </div>
            <div
              style={{
                fontSize: 17,
                fontWeight: 700,
                color: k.color ?? 'var(--n-900)',
                fontVariantNumeric: 'tabular-nums',
                marginTop: 4,
                letterSpacing: -0.4,
              }}
            >
              {k.value}
            </div>
          </div>
        ))}
      </div>
      {!isAlVencimiento && (
        <button
          onClick={onToggle}
          className="btn btn-ghost"
          style={{
            marginTop: 12,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
          }}
        >
          {expandido ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
          {expandido ? 'Ocultar tabla detallada' : `Ver los ${pagos.length} pagos en detalle`}
        </button>
      )}
    </div>
  )
}

function ReporteRow({ reporte }: { reporte: ReportePagoLite }) {
  const formaLabel: Record<string, string> = {
    EFECTIVO_VENTANILLA: 'Efectivo en ventanilla',
    DEPOSITO_BANCARIO: 'Depósito bancario',
    TRANSFERENCIA: 'Transferencia',
    CHEQUE: 'Cheque',
  }
  const estadoMeta: Record<ReportePagoLite['estado'], { label: string; color: string }> = {
    pendiente: { label: 'Pendiente', color: '#B8860B' },
    en_revision: { label: 'En revisión', color: '#3060A0' },
    conciliado: { label: 'Conciliado', color: '#0F8A57' },
    rechazado: { label: 'Rechazado', color: '#A32D2D' },
  }
  const fmt = estadoMeta[reporte.estado]
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        background: 'var(--surface)',
        borderRadius: 10,
        border: '1px solid var(--border)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--n-900)' }}>
            {formaLabel[reporte.forma_pago] ?? reporte.forma_pago}
          </div>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              color: fmt.color,
            }}
          >
            {fmt.label}
          </span>
        </div>
        <div style={{ fontSize: 11, color: 'var(--n-500)', marginTop: 2 }}>
          {formatFechaCorta(reporte.fecha_movimiento)}
          {reporte.numero_referencia ? <span> · Ref {reporte.numero_referencia}</span> : null}
        </div>
        {reporte.notas_asociado ? (
          <div style={{ fontSize: 11, color: 'var(--n-700)', marginTop: 4 }}>
            "{reporte.notas_asociado}"
          </div>
        ) : null}
      </div>
      <div
        style={{
          textAlign: 'right',
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          color: 'var(--n-900)',
          fontSize: 14,
        }}
      >
        {formatQ(reporte.monto)}
      </div>
    </div>
  )
}
